import React from 'react';
import { useTranslation } from 'react-i18next';
import { Popover } from '@airclou/ui';
import { FiltersPanel, FilterConfig, FilterOption } from '@airclou/ui';
import {
  Users,
  UserCheck,
  UserX,
  AlertCircle,
  Clock,
  Shield,
  Building2,
} from 'lucide-react';

// The status and role options the popover offers. MembersPage checks a filter
// read from the URL against these same lists, so a stale or hand-edited link
// can't send the API a value the popover never offers — and a new option can't
// be added here without the check accepting it.
const STATUS_OPTIONS = [
  { value: 'active', labelKey: 'common.active', icon: UserCheck },
  { value: 'inactive', labelKey: 'common.inactive', icon: UserX },
  { value: 'suspended', labelKey: 'common.suspended', icon: AlertCircle },
  { value: 'pending_verification', labelKey: 'common.pending', icon: Clock },
];
const ROLE_OPTIONS = [
  { value: 'owner', labelKey: 'roles.owner', icon: Shield },
  { value: 'billing_admin', labelKey: 'roles.billingAdmin', icon: Shield },
  { value: 'admin', labelKey: 'roles.admin', icon: Shield },
  { value: 'author', labelKey: 'roles.author', icon: UserCheck },
  { value: 'contributor', labelKey: 'roles.contributor', icon: UserCheck },
  { value: 'user', labelKey: 'roles.user', icon: Users },
  { value: 'viewer', labelKey: 'roles.viewer', icon: Users },
];
export const TEAM_MEMBER_STATUSES = STATUS_OPTIONS.map((o) => o.value);
export const TEAM_MEMBER_ROLES = ROLE_OPTIONS.map((o) => o.value);

export interface TeamMemberFilters {
  status: string;
  role: string;
  app: string;
}

interface TeamMemberFiltersPopoverProps {
  onFiltersChange: (filters: TeamMemberFilters) => void;
  filters: TeamMemberFilters;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  trigger: React.ReactNode;
  apps: Array<{ id: string; name: string; slug: string; description?: string }>;
}

export function TeamMemberFiltersPopover({ 
  onFiltersChange, 
  filters, 
  isOpen, 
  onOpenChange, 
  trigger,
  apps 
}: TeamMemberFiltersPopoverProps) {
  const { t } = useTranslation('dashboard');

  const statusOptions: FilterOption[] = STATUS_OPTIONS.map(({ value, labelKey, icon }) => ({
    value, label: t(labelKey), count: 0, icon,
  }));

  const roleOptions: FilterOption[] = ROLE_OPTIONS.map(({ value, labelKey, icon }) => ({
    value, label: t(labelKey), count: 0, icon,
  }));

  // Convert apps to filter options, excluding Dashboard (it's always accessible)
  const appOptions: FilterOption[] = apps
    .filter(app => app.slug !== 'dashboard')
    .map(app => ({
      value: app.id,
      label: app.name,
      count: 0, // Will be updated dynamically
      icon: Building2
    }));

  const filterConfigs: FilterConfig[] = [
    {
      key: 'status',
      label: t('common.status'),
      type: 'single',
      options: statusOptions,
      defaultValue: 'all',
      icon: UserCheck
    },
    {
      key: 'role',
      label: t('common.role'),
      type: 'single',
      options: roleOptions,
      defaultValue: 'all',
      icon: Shield
    },
    {
      key: 'app',
      label: t('filters.application'),
      type: 'single',
      options: appOptions,
      defaultValue: 'all',
      icon: Building2
    },
  ];

  return (
    <Popover
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      trigger={trigger}
      align="start"
      spacing="minimal"
      contentClassName="w-96"
    >
      <div className="p-4">
        <h4 className="font-medium text-foreground">{t('filters.teamMemberFilters')}</h4>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          {t('filters.filterDescription')}
        </p>
        <div>
          <FiltersPanel<TeamMemberFilters>
            isOpen={true}
            onClose={() => onOpenChange(false)}
            onFiltersChange={onFiltersChange}
            filters={filters}
            filterConfigs={filterConfigs}
            isEmbedded={true}
          />
        </div>
      </div>
    </Popover>
  );
}
